import type { NodePropertyName } from '@salescore/core'
import { Select } from 'antd'
import { Array, pipe } from 'effect'
import { useEffect, useMemo } from 'react'

import type { KpiFormCurrentNodeProperty } from '../../../../recoil/view/selectors/kpiFormSelector'

export const PropertySelect = ({
  properties,
  value,
  placeholder,
  disabled,
  allowClear,
  onChange,
}: {
  properties: KpiFormCurrentNodeProperty[]
  value: NodePropertyName | undefined
  placeholder?: string
  allowClear?: boolean
  disabled?: boolean
  onChange: (nodePropertyName?: NodePropertyName) => void
}) => {
  const { options, picked } = useMemo(() => {
    const options = pipe(
      properties,
      Array.map((x) => {
        // eslint-disable-next-line sonarjs/no-dead-store,sonarjs/no-unused-vars
        const { property, model, node } = x
        return {
          value: propertyWithModelToSelectOptionValue(x),
          // eslint-disable-next-line sonarjs/todo-tag
          // TODO: labelどうするか問題
          key: `${node.nodePathAsLabel.join(': ')}: ${property.label} - ${node.node.name}.${property.name}`,
          label: (
            <>
              {node.nodePathAsLabel.join(': ')}: {property.label}
              <span className="ml-2 text-xs text-gray-500">
                - {node.node.name}.{property.name}
              </span>
            </>
          ),
          property: x,
        }
      }),
      Array.dedupeWith((a, b) => a.value === b.value),
    )
    const valueAsString = value === undefined ? '' : nodePropertyNameToSelectOptionValue(value)
    const picked = options.find((x) => x.value === valueAsString)
    return { options, picked }
  }, [properties, value])

  useEffect(() => {
    // options,valueが切り替わって選択している値がなくなったら、値をリセットする
    if (picked === undefined && value !== undefined) {
      onChange()
    }
  }, [picked?.value])

  const filterOption = (input: string, option?: { key: string }) =>
    (option?.key ?? '').toLowerCase().includes(input.toLowerCase())

  return (
    <Select
      allowClear={allowClear}
      filterOption={filterOption}
      style={{ width: `100%` }}
      placeholder={placeholder}
      disabled={disabled}
      options={options}
      value={picked?.value ?? ''}
      onChange={(value) => {
        const picked = options.find((x) => x.value === value)
        if (picked === undefined) {
          onChange()
          return
        }
        const { property } = picked
        onChange({
          nodeName: property.node.node.name,
          modelName: property.model.name,
          propertyName: property.property.name,
        })
      }}
      showSearch
      optionRender={(option) => <span className="whitespace-normal break-words ">{option.label}</span>}
    />
  )
}

function propertyWithModelToSelectOptionValue(x: KpiFormCurrentNodeProperty) {
  return JSON.stringify([x.node.node.name, x.model.name, x.property.name])
}

function nodePropertyNameToSelectOptionValue(x: NodePropertyName) {
  return JSON.stringify([x.nodeName, x.modelName, x.propertyName])
}
